import { addDays, differenceInDays, isBefore } from 'date-fns';
import { InvoicePaymentDisplayStatusType } from 'anchor-scout-common/types';
import { Accounting, InvoicePaymentMethodStatus } from "../../types/api.model";
import { ApprovalMethodNoticePeriodType } from "../approval-method-notice-period";
import { InvoiceStatus } from "./model";
export function isPending(invoice) {
  return invoice.status.type === InvoiceStatus.Pending;
}
export function isPaid(invoice) {
  return invoice.status.type === InvoiceStatus.Paid;
}
export function isConfirmed(invoice) {
  return invoice.status.type === InvoiceStatus.PaymentConfirmed;
}
export function isProcessing(invoice) {
  return invoice.status.type === InvoiceStatus.ProcessingCharge;
}
export function isPaidExternally(invoice) {
  return invoice.status.type === InvoiceStatus.PaidExternally;
}
export function isVoided(invoice) {
  return invoice.status.type === InvoiceStatus.Voided;
}
export function isRefunded(invoice) {
  return invoice.status.type === InvoiceStatus.Refunded;
}
export function isPartiallyRefunded(invoice) {
  return invoice.status.type === InvoiceStatus.PartiallyRefunded;
}
export function totalRefunded(invoice) {
  var _invoice$payment;
  return ((_invoice$payment = invoice.payment) === null || _invoice$payment === void 0 ? void 0 : _invoice$payment.totalRefunded()) || '0';
}
export function hasRefundFees(invoice) {
  var _invoice$payment2;
  return ((_invoice$payment2 = invoice.payment) === null || _invoice$payment2 === void 0 ? void 0 : _invoice$payment2.hasRefundFees()) || false;
}
export function isPaymentStopped(invoice) {
  return invoice.status.type === InvoiceStatus.PaymentStopped;
}
export function isPaymentFailed(invoice) {
  return invoice.status.type === InvoiceStatus.PaymentFailed;
}
export function isRequiresPaymentApproval(invoice) {
  return invoice.status.type === InvoiceStatus.RequiresPaymentApproval;
}
export function isPaymentActionRequired(invoice) {
  return invoice.status.type === InvoiceStatus.PaymentActionRequired;
}
export function isDisputed(invoice) {
  return invoice.status.type === InvoiceStatus.PaymentDisputed;
}
export function isRefundRequested(paymentStatus) {
  return !!paymentStatus && paymentStatus.type === InvoicePaymentDisplayStatusType.RefundRequested;
}
export function isRefundFailed(paymentStatus) {
  return !!paymentStatus && paymentStatus.type === InvoicePaymentDisplayStatusType.RefundFailed;
}
export function statusType(invoice) {
  return invoice.status.type;
}
export function paymentMethodStatus(invoice) {
  return invoice.paymentMethodStatus;
}
export function disputedMessage(invoice) {
  return invoice.status.type === InvoiceStatus.PaymentDisputed ? invoice.status.paymentDisputed.message : null;
}
export function dueDate(invoice) {
  return invoice.dueDate;
}
export function formattedNumber(_ref) {
  let {
    formattedNumber
  } = _ref;
  return formattedNumber;
}
export function customerName(inv) {
  return inv.relationship.customer.name;
}
export function issuedOn(_ref2) {
  let {
    issuedOn
  } = _ref2;
  return issuedOn;
}
export function issueDate(invoice) {
  return invoice.issuedOn || invoice.createdOn;
}

//in case user billed now with a past issue date, we should use the createdOn date
export function paymentProcessingDate(invoice) {
  return isBefore(invoice.rawDueDate, invoice.rawCreatedOn) ? invoice.createdOn : invoice.dueDate;
}
export function paymentFailureMessage(_ref3) {
  let {
    status
  } = _ref3;
  return status.type === InvoiceStatus.PaymentFailed ? status.paymentFailed.message : undefined;
}
export function paymentFailureCode(_ref4) {
  let {
    status
  } = _ref4;
  return status.type === InvoiceStatus.PaymentFailed ? status.paymentFailed.failureCode : undefined;
}
export function processingAction(_ref5) {
  let {
    status
  } = _ref5;
  if (status.type === InvoiceStatus.PaymentActionRequired) {
    const {
      processingAction
    } = status.paymentActionRequired;
    return {
      stripeUrl: processingAction.url,
      clientSecret: processingAction.clientSecret
    };
  } else {
    return {
      stripeUrl: null,
      clientSecret: null
    };
  }
}
export function isPaymentMethodUnset(invoice) {
  return invoice.paymentMethodStatus === InvoicePaymentMethodStatus.PaymentMethodNotSet;
}
export function isPaymentMethodUnverified(invoice) {
  return invoice.paymentMethodStatus === InvoicePaymentMethodStatus.PaymentMethodUnverified;
}
export function isStableIntegrationStatus(invoice) {
  const trueFnc = () => true;
  return invoice.integration.accept({
    whenPendingSync: trueFnc,
    whenInvoiceSyncFailed: trueFnc,
    whenPaymentSyncFailed: trueFnc,
    whenRefundSyncFailed: trueFnc,
    whenVoidedRecorded: trueFnc,
    whenPaymentRecorded: trueFnc,
    whenRefundRecorded: trueFnc,
    whenInvoiceRecorded: () => isPending(invoice) || isPaidExternally(invoice) || isPaymentStopped(invoice) || isPaid(invoice) && invoice.isTotalAmountZero || isRequiresPaymentApproval(invoice) || isPaymentActionRequired(invoice)
  });
}
export const qboIntegration = _ref6 => {
  let {
    integration
  } = _ref6;
  return integration;
};
export const xeroIntegration = _ref7 => {
  let {
    xeroIntegration
  } = _ref7;
  return xeroIntegration.getIntegration();
};
export function paymentStopReason(invoice) {
  return invoice.status.type === InvoiceStatus.PaymentStopped ? invoice.status.paymentStopped.reason : '';
}
export const autoApprovalDate = invoice => {
  const {
    status,
    createdOn
  } = invoice;
  const approvalMethod = status.type === InvoiceStatus.RequiresPaymentApproval ? status.requiresPaymentApproval.approvalMethod : null;
  return approvalMethod && approvalMethod.type === ApprovalMethodNoticePeriodType.Automatic ? addDays(new Date(createdOn), approvalMethod.auto.noticePeriodDays) : null;
};
export const totalAmount = _ref8 => {
  let {
    totalAmount
  } = _ref8;
  return totalAmount;
};
export const id = _ref9 => {
  let {
    id
  } = _ref9;
  return id;
};
export const itemName = _ref10 => {
  let {
    name
  } = _ref10;
  return name;
};
export const itemQuantity = _ref11 => {
  let {
    quantity
  } = _ref11;
  return quantity || '';
};
export const itemRate = _ref12 => {
  let {
    rate
  } = _ref12;
  return rate;
};
export const itemDiscount = _ref13 => {
  let {
    discount
  } = _ref13;
  return discount;
};
export const itemTotalAmount = _ref14 => {
  let {
    totalAmount
  } = _ref14;
  return totalAmount;
};
export const subItemName = _ref15 => {
  let {
    name
  } = _ref15;
  return name;
};
export const subItemRate = _ref16 => {
  let {
    rate
  } = _ref16;
  return rate;
};
export const subItemDiscount = _ref17 => {
  let {
    discount
  } = _ref17;
  return discount;
};
export const subItemTotalAmount = _ref18 => {
  let {
    totalAmount
  } = _ref18;
  return totalAmount;
};
export const subItemUnitName = _ref19 => {
  let {
    unitName
  } = _ref19;
  return unitName;
};
export const vendorName = inv => inv.relationship.vendor.name;
export const otherPartyCompanyName = (inv, isVendor) => isVendor ? customerName(inv) : vendorName(inv);
export const relationshipId = inv => inv.relationship.id;
export const relationshipTitle = inv => inv.relationship.title;
export function isSynced(inv) {
  return inv.integration.isSynced();
}
export function isNotSynced(inv) {
  return !!inv.integration && !inv.integration.isSynced();
}
export const groupedLineItems = item => item.groupedLineItems;
export const isGrouped = item => groupedLineItems(item).length > 0;
export const rawTotalAmount = _ref20 => {
  let {
    rawTotalAmount
  } = _ref20;
  return rawTotalAmount;
};
export const accounting = _ref21 => {
  let {
    accounting,
    invoice
  } = _ref21;
  switch (accounting) {
    case Accounting.QBO:
      return {
        integration: qboIntegration(invoice)
      };
    case Accounting.Xero:
      return {
        integration: xeroIntegration(invoice)
      };
  }
};
export const payment = _ref22 => {
  let {
    payment
  } = _ref22;
  return payment;
};
export const numRefunds = invoice => {
  var _invoice$payment$refu, _invoice$payment3;
  return (_invoice$payment$refu = (_invoice$payment3 = invoice.payment) === null || _invoice$payment3 === void 0 || (_invoice$payment3 = _invoice$payment3.refunds) === null || _invoice$payment3 === void 0 ? void 0 : _invoice$payment3.length) !== null && _invoice$payment$refu !== void 0 ? _invoice$payment$refu : null;
};
export const daysSincePayment = invoice => {
  if (invoice.status.type === InvoiceStatus.Paid) {
    const paidTimestamp = invoice.status.paid.paymentDate;
    return differenceInDays(new Date().setHours(0, 0, 0, 0), new Date(paidTimestamp).setHours(0, 0, 0, 0));
  } else {
    return null;
  }
};